import React from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus } from 'react-icons/fa';
import SelectTextField from '../../../../molecules/SelectTextField';
import CurrencyInput from '../../../../molecules/CurrencyInput';
import TextEdit from '../../../../molecules/TextEdit';
import { MachineryItem } from '../../types/operations';

interface MachineryItemFormProps {
    items: MachineryItem[];
    onAdd: (item: MachineryItem) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
}

const MIN_ROWS = 5;

const generateId = (): string =>
    crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const legalRegimeOptions = [
    { value: 'AF', label: 'Alienação Fiduciária' },
    { value: 'PENHOR', label: 'Penhor' },
];

const assetTypeOptions = [
    { value: 'HARVESTER', label: 'Colheitadeira' },
    { value: 'TRACTOR', label: 'Trator' },
    { value: 'SPRAYER', label: 'Pulverizador' },
    { value: 'PLANTER', label: 'Plantadeira' },
    { value: 'TRUCK', label: 'Caminhão' },
    { value: 'OTHER', label: 'Outro' },
];

const conditionOptions = [
    { value: 'NEW', label: 'Novo' },
    { value: 'USED', label: 'Usado' },
    { value: 'REFURBISHED', label: 'Recondicionado' },
];

const formatCurrency = (val: number): string =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const MachineryItemForm: React.FC<MachineryItemFormProps> = ({ items, onAdd, onRemove, disabled = false }) => {
    const legalRegime = useSignal('');
    const assetType = useSignal('');
    const name = useSignal('');
    const value = useSignal(0);
    const brand = useSignal('');
    const model = useSignal('');
    const yearManufacture = useSignal('');
    const serialNumber = useSignal('');
    const plate = useSignal('');
    const condition = useSignal('');
    const description = useSignal('');

    const resetForm = () => {
        legalRegime.value = '';
        assetType.value = '';
        name.value = '';
        value.value = 0;
        brand.value = '';
        model.value = '';
        yearManufacture.value = '';
        serialNumber.value = '';
        plate.value = '';
        condition.value = '';
        description.value = '';
    };

    const handleAdd = () => {
        if (!legalRegime.value) { toast.error('Selecione o regime legal'); return; }
        if (!assetType.value) { toast.error('Selecione o tipo de equipamento'); return; }
        if (!name.value.trim()) { toast.error('Informe o nome/descrição'); return; }
        if (value.value <= 0) { toast.error('Informe o valor'); return; }

        onAdd({
            collateral_id: generateId(),
            legal_regime: legalRegime.value as MachineryItem['legal_regime'],
            asset_type: assetType.value as MachineryItem['asset_type'],
            name: name.value.trim(),
            value: value.value,
            brand: brand.value.trim() || undefined,
            model: model.value.trim() || undefined,
            year_manufacture: yearManufacture.value ? Number(yearManufacture.value) : undefined,
            serial_number: serialNumber.value.trim() || undefined,
            plate: plate.value.trim() || undefined,
            condition: (condition.value as MachineryItem['condition']) || undefined,
            description: description.value.trim() || undefined,
        });
        resetForm();
    };

    const emptyRowCount = Math.max(0, MIN_ROWS - items.length);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3">
                <div className="col-span-3">
                    <SelectTextField label="Regime Legal *" items={legalRegimeOptions} value={legalRegime.value} onChange={(e) => { legalRegime.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <SelectTextField label="Tipo Equipamento *" items={assetTypeOptions} value={assetType.value} onChange={(e) => { assetType.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <TextEdit label="Nome/Descrição *" value={name.value} onChange={(e) => { name.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-3">
                    <CurrencyInput label="Valor (R$) *" value={value.value} onChange={(v) => { value.value = v ?? 0; }} disabled={disabled} fractionDigits={2} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Marca" value={brand.value} onChange={(e) => { brand.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Modelo" value={model.value} onChange={(e) => { model.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Ano Fab." type="number" value={yearManufacture.value} onChange={(e) => { yearManufacture.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <TextEdit label="Placa" value={plate.value} onChange={(e) => { plate.value = (e.target as HTMLInputElement).value; }} disabled={disabled} />
                </div>
                <div className="col-span-2">
                    <SelectTextField label="Condição" items={conditionOptions} value={condition.value} onChange={(e) => { condition.value = e.target.value; }} disabled={disabled} />
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button" onClick={handleAdd} disabled={disabled} className="flex items-center gap-1 px-3 py-2 mb-4 bg-gray-600 dark:bg-gray-500 text-white text-sm rounded hover:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
                        <FaPlus size={12} /> Adicionar
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-600 rounded">
                <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
                    <thead className="text-xs uppercase bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                        <tr>
                            <th className="px-3 py-2">Regime</th>
                            <th className="px-3 py-2">Tipo</th>
                            <th className="px-3 py-2">Nome</th>
                            <th className="px-3 py-2">Valor</th>
                            <th className="px-3 py-2">Marca/Modelo</th>
                            <th className="px-3 py-2">Ano</th>
                            <th className="px-3 py-2">Placa</th>
                            <th className="px-3 py-2 w-16">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={item.collateral_id} className={`${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">{item.legal_regime === 'AF' ? 'Alienação Fid.' : 'Penhor'}</td>
                                <td className="px-3 py-2">{assetTypeOptions.find(o => o.value === item.asset_type)?.label || item.asset_type}</td>
                                <td className="px-3 py-2">{item.name}</td>
                                <td className="px-3 py-2">{formatCurrency(item.value)}</td>
                                <td className="px-3 py-2">{[item.brand, item.model].filter(Boolean).join(' ') || '-'}</td>
                                <td className="px-3 py-2">{item.year_manufacture || '-'}</td>
                                <td className="px-3 py-2">{item.plate || '-'}</td>
                                <td className="px-3 py-2">
                                    <button type="button" onClick={() => onRemove(index)} disabled={disabled} className="text-red-600 dark:text-red-400 hover:text-red-800 disabled:opacity-50" title="Remover">
                                        <FaTimes size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`empty-${i}`} className={`${(items.length + i) % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-750'} border-b border-gray-200 dark:border-gray-600`}>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                                <td className="px-3 py-2">&nbsp;</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MachineryItemForm;
